import { useCallback, useRef } from "react";
import React from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ContentProcessingToast } from "@/components/content/ContentProcessingToast";

type ContentKind = "pdf" | "doc" | "web" | "text" | "audio" | "video" | "youtube" | "recording" | string;

interface ShowProcessingToastArgs {
  contentId: string;
  contentType: ContentKind;
  title: string;
  phrases: string[];
  readyLabel: string;
  onReady?: () => void;
  fallbackMs?: number;
}

const MEDIA: ContentKind[] = ["audio", "video", "youtube", "recording"];
const DOC: ContentKind[] = ["pdf", "doc", "ppt", "web", "text"];

export function useContentProcessingToast() {
  const activeRef = useRef<Set<string | number>>(new Set());

  const showProcessingToast = useCallback(
    ({
      contentId,
      contentType,
      title,
      phrases,
      readyLabel,
      onReady,
      fallbackMs = 300000,
    }: ShowProcessingToastArgs) => {
      const isMedia = MEDIA.includes(contentType);
      const isDoc = DOC.includes(contentType);

      let target = 5;
      let display = 0;
      let phraseIdx = 0;
      let done = false;
      let cleaned = false;

      const toastId = `content-processing-${contentId}`;
      activeRef.current.add(toastId);

      const renderToast = () => {
        const currentPhrase = done ? readyLabel : phrases[phraseIdx % phrases.length];
        toast.custom(
          () =>
            React.createElement(ContentProcessingToast, {
              title,
              phrase: currentPhrase,
              progress: display,
              done,
            }),
          { id: toastId, duration: Infinity }
        );
      };

      renderToast();

      // Smooth display progress easing toward target.
      const easeInterval = window.setInterval(() => {
        const cap = done ? 100 : Math.max(target - 1, 0);
        if (display < cap) {
          // Step bigger when far from target.
          const gap = cap - display;
          const step = Math.max(1, Math.round(gap * 0.08));
          display = Math.min(cap, display + step);
          renderToast();
        }
      }, 400);

      // Phrase shuffler.
      const phraseInterval = window.setInterval(() => {
        if (done) return;
        phraseIdx = (phraseIdx + 1) % phrases.length;
        renderToast();
      }, 1600);

      const cleanup = (navigate: boolean) => {
        if (cleaned) return;
        cleaned = true;
        window.clearInterval(easeInterval);
        window.clearInterval(phraseInterval);
        try {
          channel.unsubscribe();
        } catch {
          /* noop */
        }
        window.clearTimeout(fallbackTimer);
        activeRef.current.delete(toastId);

        // Snap to 100, show success briefly, then dismiss + navigate.
        done = true;
        display = 100;
        renderToast();
        window.setTimeout(() => {
          toast.dismiss(toastId);
          if (navigate && onReady) onReady();
        }, 450);
      };

      const fail = () => {
        if (cleaned) return;
        cleaned = true;
        window.clearInterval(easeInterval);
        window.clearInterval(phraseInterval);
        try {
          channel.unsubscribe();
        } catch {
          /* noop */
        }
        window.clearTimeout(fallbackTimer);
        activeRef.current.delete(toastId);
        toast.dismiss(toastId);
        toast.error("Conversion failed. Please try again later.");
      };

      const handleUpdate = (row: any) => {
        if (!row) return;

        // Conversion milestone.
        if (
          contentType === "pdf" ||
          row.conversion_status === "ready" ||
          row.conversion_status === "not_needed"
        ) {
          target = Math.max(target, 35);
        }

        // Extraction / transcription milestone.
        if (isMedia && row.transcript_status === "ready") {
          target = Math.max(target, 70);
        }
        if (isDoc && row.text_extraction_status === "ready") {
          target = Math.max(target, 70);
        }

        if (
          row.conversion_status === "failed" ||
          row.text_extraction_status === "failed" ||
          row.chapters_status === "failed"
        ) {
          fail();
          return;
        }

        // Final ready condition mirrors DashboardHeroStudy logic.
        let ready = false;
        if (contentType === "pdf") {
          if (row.chapters_status === "ready") ready = true;
        } else if (isMedia) {
          if (row.transcript_status === "ready") ready = true;
        } else if (isDoc) {
          if (row.chapters_status === "ready" || row.text_extraction_status === "ready") {
            ready = true;
          }
        }

        if (ready) {
          target = 100;
          cleanup(true);
        }
      };

      const channel = supabase
        .channel(`content-processing-${contentId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "content",
            filter: `id=eq.${contentId}`,
          },
          (payload) => handleUpdate(payload.new)
        )
        .subscribe();

      const fallbackTimer = window.setTimeout(() => {
        cleanup(true);
      }, fallbackMs);

      return () => cleanup(false);
    },
    []
  );

  return { showProcessingToast };
}
